import React from 'react';
import './ServicesPage.css';

interface ServicesPageProps {
  onNavigateToQuote: () => void;
  onNavigateToClaims: () => void;
  onNavigateToChatbot: () => void;
}

const ServicesPage: React.FC<ServicesPageProps> = ({ onNavigateToQuote, onNavigateToClaims, onNavigateToChatbot }) => {
  const handleChatbotClick = () => {
    onNavigateToChatbot();
  };

  return (
    <main className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-content">
          <h1>Our Insurance Services</h1>
          <p>Comprehensive coverage for all your insurance needs. We're here to protect what matters most to you.</p>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services-detail">
        <div className="container">
          <h2>Core Services</h2>
          <div className="services-grid">
            <div className="service-detail-card">
              <div className="service-icon">💰</div>
              <h3>Insurance Quotes</h3>
              <p>Get instant, personalized quotes for all types of insurance. Our AI-powered system compares rates from multiple providers to find you the best deals.</p>
              <ul>
                <li>✓ Instant online quotes</li>
                <li>✓ Compare multiple providers</li>
                <li>✓ No hidden fees</li>
                <li>✓ Save up to 40% on premiums</li>
              </ul>
              <button className="service-btn" onClick={onNavigateToQuote}>Get Your Quote</button>
            </div>

            <div className="service-detail-card">
              <div className="service-icon">💬</div>
              <h3>AI Insurance Assistant</h3>
              <p>24/7 AI-powered chatbot that answers your insurance questions instantly. Get expert advice anytime, anywhere.</p>
              <ul>
                <li>✓ Available 24/7</li>
                <li>✓ Instant expert answers</li>
                <li>✓ Policy management help</li>
                <li>✓ Claims guidance</li>
              </ul>
              <button className="service-btn" onClick={handleChatbotClick}>Chat Now</button>
            </div>

            <div className="service-detail-card">
              <div className="service-icon">📋</div>
              <h3>Claims Processing</h3>
              <p>Fast and easy claims filing with real-time status tracking. Our streamlined process gets you back on track quickly.</p>
              <ul>
                <li>✓ Simple online filing</li>
                <li>✓ Document upload system</li>
                <li>✓ Real-time claim tracking</li>
                <li>✓ Fast processing times</li>
              </ul>
              <button className="service-btn" onClick={onNavigateToClaims}>File a Claim</button>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Types */}
      <section className="insurance-types">
        <div className="container">
          <h2>Insurance Coverage Types</h2>
          <div className="types-grid">
            <div className="type-card">
              <div className="type-header">
                <div className="type-icon">🚗</div>
                <h3>Auto Insurance</h3>
              </div>
              <div className="type-content">
                <p>Comprehensive vehicle protection including liability, collision, and comprehensive coverage.</p>
                <div className="coverage-list">
                  <h4>What's Covered:</h4>
                  <ul>
                    <li>Liability protection</li>
                    <li>Collision damage</li>
                    <li>Comprehensive coverage</li>
                    <li>Uninsured motorist protection</li>
                    <li>Personal injury protection</li>
                  </ul>
                </div>
                <div className="pricing">
                  <span className="price-label">Starting from</span>
                  <span className="price">$89/month</span>
                </div>
              </div>
            </div>

            <div className="type-card">
              <div className="type-header">
                <div className="type-icon">🏠</div>
                <h3>Home Insurance</h3>
              </div>
              <div className="type-content">
                <p>Protect your home and belongings from fire, theft, natural disasters, and liability claims.</p>
                <div className="coverage-list">
                  <h4>What's Covered:</h4>
                  <ul>
                    <li>Dwelling protection</li>
                    <li>Personal property</li>
                    <li>Liability coverage</li>
                    <li>Additional living expenses</li>
                    <li>Medical payments</li>
                  </ul>
                </div>
                <div className="pricing">
                  <span className="price-label">Starting from</span>
                  <span className="price">$125/month</span>
                </div>
              </div>
            </div>

            <div className="type-card">
              <div className="type-header">
                <div className="type-icon">👨‍👩‍👧‍👦</div>
                <h3>Life Insurance</h3>
              </div>
              <div className="type-content">
                <p>Financial security for your loved ones with term and whole life insurance options.</p>
                <div className="coverage-list">
                  <h4>What's Covered:</h4>
                  <ul>
                    <li>Death benefit payout</li>
                    <li>Accidental death coverage</li>
                    <li>Cash value (whole life)</li>
                    <li>Terminal illness benefit</li>
                    <li>Disability waiver</li>
                  </ul>
                </div>
                <div className="pricing">
                  <span className="price-label">Starting from</span>
                  <span className="price">$35/month</span>
                </div>
              </div>
            </div>

            <div className="type-card">
              <div className="type-header">
                <div className="type-icon">🏥</div>
                <h3>Health Insurance</h3>
              </div>
              <div className="type-content">
                <p>Comprehensive medical coverage including preventive care, emergency services, and prescription drugs.</p>
                <div className="coverage-list">
                  <h4>What's Covered:</h4>
                  <ul>
                    <li>Doctor visits</li>
                    <li>Hospital stays</li>
                    <li>Prescription medications</li>
                    <li>Preventive care</li>
                    <li>Emergency services</li>
                  </ul>
                </div>
                <div className="pricing">
                  <span className="price-label">Starting from</span>
                  <span className="price">$275/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="additional-services">
        <div className="container">
          <h2>Additional Services</h2>
          <div className="additional-grid">
            <div className="additional-card">
              <div className="additional-icon">📊</div>
              <h3>Risk Assessment</h3>
              <p>Comprehensive analysis of your insurance needs and risk factors to optimize your coverage.</p>
            </div>

            <div className="additional-card">
              <div className="additional-icon">💼</div>
              <h3>Business Insurance</h3>
              <p>Protect your business with commercial liability, property, and workers' compensation insurance.</p>
            </div>

            <div className="additional-card">
              <div className="additional-icon">🎓</div>
              <h3>Insurance Education</h3>
              <p>Free resources and guides to help you understand insurance and make informed decisions.</p>
            </div>

            <div className="additional-card">
              <div className="additional-icon">🔄</div>
              <h3>Policy Management</h3>
              <p>Easy online tools to manage your policies, make payments, and update your information.</p>
            </div>

            <div className="additional-card">
              <div className="additional-icon">📞</div>
              <h3>24/7 Support</h3>
              <p>Round-the-clock customer service via phone, chat, or email for all your insurance needs.</p>
            </div>

            <div className="additional-card">
              <div className="additional-icon">🏆</div>
              <h3>Claims Advocacy</h3>
              <p>Personal claims advocates to guide you through the process and ensure fair settlements.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-us">
        <div className="container">
          <h2>Why Choose PolicyPal?</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-number">01</div>
              <div className="benefit-content">
                <h3>Best Rates Guaranteed</h3>
                <p>We compare rates from over 50 insurance providers to ensure you get the best possible price.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-number">02</div>
              <div className="benefit-content">
                <h3>Fast Claims Processing</h3>
                <p>Our streamlined digital process ensures claims are processed 40% faster than industry average.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-number">03</div>
              <div className="benefit-content">
                <h3>Award-Winning Service</h3>
                <p>Recognized by industry leaders for excellence in customer service and innovation.</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-number">04</div>
              <div className="benefit-content">
                <h3>Technology-Driven</h3>
                <p>AI-powered tools and digital platforms make managing your insurance simple and efficient.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of satisfied customers who trust PolicyPal with their insurance needs.</p>
            <div className="cta-buttons">
              <button className="btn-primary" onClick={onNavigateToQuote}>Get Free Quote</button>
              <button className="btn-secondary" onClick={handleChatbotClick}>Ask Questions</button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;